import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import classNames from 'classnames';
import Button from '../../../../../components/button/button';
import CommentItem from './comment-item';

const commentsQuery = graphql`
    query commentsListQuery($postId: String!, $includeIsAuthorized: Boolean!, $first: Int!, $after: String) {
        ...commentsList_query @arguments(postId: $postId, first: $first, after: $after, includeIsAuthorized: $includeIsAuthorized)
    }
`;

const CommentsList = React.memo(({postId, currentUserId, isAuthorized, highlightId}) => {
    const queryData = useLazyLoadQuery(commentsQuery, {
        postId,
        includeIsAuthorized: isAuthorized,
        first: 8,
        after: null
    });
    
    const {data, loadNext, hasNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql`
            fragment commentsList_query on Query
            @refetchable(queryName: "CommentsListRefetchQuery")
            @argumentDefinitions(
                postId: {type: "String!"}
                first: {type: "Int", defaultValue: 8}
                after: {type: "String"}
                includeIsAuthorized: {type: "Boolean", defaultValue: false}
            ) {
                vibePostById(id: $postId) {
                    id
                    comments(first: $first, after: $after) @connection(key: "CommentsList_comments") {
                        edges {
                            node {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                likedByCurrentUser @include(if: $includeIsAuthorized)
                                stemLikesCount
                                stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                viewsCount
                                viewedByCurrentUser @include(if: $includeIsAuthorized)
                                commentedByCurrentUser @include(if: $includeIsAuthorized)
                                quotedByCurrentUser @include(if: $includeIsAuthorized)
                                quotesCount
                                commentsCount
                                sharesCount
                                sharedByCurrentUser @include(if: $includeIsAuthorized)
                                sharedPost {
                                    id
                                    message
                                    media
                                    created
                                    removed
                                    spam
                                    sharesCount
                                    likesCount
                                    likedByCurrentUser @include(if: $includeIsAuthorized)
                                    stemLikesCount
                                    stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                    viewsCount
                                    viewedByCurrentUser @include(if: $includeIsAuthorized)
                                    commentedByCurrentUser @include(if: $includeIsAuthorized)
                                    quotedByCurrentUser @include(if: $includeIsAuthorized)
                                    commentsCount
                                    quotesCount
                                    author {
                                        id
                                        vibe {
                                            username
                                        }
                                        followingUser @include(if: $includeIsAuthorized)
                                    }
                                }
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                quotedPost {
                                    id
                                    message
                                    media
                                    created
                                    removed
                                    spam
                                    sharesCount
                                    likesCount
                                    likedByCurrentUser @include(if: $includeIsAuthorized)
                                    stemLikesCount
                                    stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                    viewsCount
                                    viewedByCurrentUser @include(if: $includeIsAuthorized)
                                    quotedByCurrentUser @include(if: $includeIsAuthorized)
                                    quotesCount
                                    poll {
                                        id
                                        options
                                        optionVotesCounts
                                        pollVotesCount
                                        votedByCurrentUser
                                        votedOptionIndex
                                    }
                                    author {
                                        id
                                        vibe {
                                            username
                                        }
                                        followingUser @include(if: $includeIsAuthorized)
                                    }
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                    followingUser @include(if: $includeIsAuthorized)
                                }
                            }
                        }
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                    }
                }
            }
        `,
        queryData
    );
    
    const comments = data.vibePostById?.comments?.edges || [];
    const commentRefs = React.useRef(new Map());

    const getCommentRef = React.useCallback((commentId) => {
        if (!commentRefs.current.has(commentId)) {
            commentRefs.current.set(commentId, React.createRef());
        }
        return commentRefs.current.get(commentId);
    }, []);

    return (
        <div>
            {comments.length === 0 && (
                <div className='text-md color-gray-500 padding-left-0dot75rem'>
                    <FormattedMessage defaultMessage='No comments yet' />
                </div>
            )}
            {comments.length > 0 && (
                <div>
                    {comments.map((edge, index) => {
                        const comment = edge.node;
                        return (
                            <div key={comment.id} className={classNames({'padding-top-1rem': index > 0})}>
                                <div className='position-relative'>
                                    <div
                                        className='position-absolute top-1dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-gray-500 pointer-events-none'
                                        style={{left: '-0.8rem', width: '0.6rem'}}
                                    />
                                    <CommentItem
                                        comment={comment}
                                        currentUserId={currentUserId} 
                                        isAuthorized={isAuthorized} 
                                        getCommentRef={getCommentRef}
                                        highlightId={highlightId}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {hasNext && (
                <div className='padding-top-1rem'>
                    <div className='position-relative'>
                        <div
                            className='position-absolute top-0dot75rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-gray-500 pointer-events-none'
                            style={{left: '-0.8rem', width: '0.6rem'}}
                        />
                        <div className='display-flex justify-content-flex-start'>
                            <Button {...{
                                size: 'md',
                                color: 'primary',
                                clickHandler: () => loadNext(8),
                                isLoading: isLoadingNext,
                                disabled: isLoadingNext
                            }}>
                                <FormattedMessage defaultMessage='More comments' />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default CommentsList;
